import { ChatbotApi } from '@liks/backend-svc-api/esm/apis/ChatbotApi';
import type { ChatbotMembership } from '@liks/backend-svc-api/esm/models/index';
import type {
  AccessibleChatbotWithIdentity,
  ChatbotConfigUpdate,
  ChatbotConversationWithIdentity,
  ChatbotMemberRole,
  ChatbotMembershipWithIdentity,
  ChatbotMessageWithIdentity,
  ChatbotTrainingExampleWithIdentity,
  ChatbotWithIdentity,
} from '../types/chatbot';
import { backendApiConfiguration } from './backendApi';
import * as chatbotConversationOps from './chatbotConversationOps';
import * as chatbotCrudOps from './chatbotCrudOps';
import * as chatbotMembershipOps from './chatbotMembershipOps';
import * as chatbotTrainingOps from './chatbotTrainingOps';

class ChatbotService {
  private readonly chatbotApi = new ChatbotApi(backendApiConfiguration);

  listChatbotsForKnowledgeBase(
    knowledgeBaseId: string,
    limit = 1000,
  ): Promise<ChatbotWithIdentity[]> {
    return chatbotCrudOps.listChatbotsForKnowledgeBase(
      this.chatbotApi,
      knowledgeBaseId,
      limit,
    );
  }

  listAccessibleChatbots(): Promise<AccessibleChatbotWithIdentity[]> {
    return chatbotCrudOps.listAccessibleChatbots(this.chatbotApi);
  }

  getChatbot(chatbotId: string): Promise<ChatbotWithIdentity> {
    return chatbotCrudOps.getChatbot(this.chatbotApi, chatbotId);
  }

  createChatbot(input: {
    knowledgeBaseId: string;
    name: string;
  }): Promise<ChatbotWithIdentity> {
    return chatbotCrudOps.createChatbot(this.chatbotApi, input);
  }

  updateChatbot(
    chatbot: ChatbotWithIdentity,
    updates: ChatbotConfigUpdate,
  ): Promise<ChatbotWithIdentity> {
    return chatbotCrudOps.updateChatbot(this.chatbotApi, chatbot, updates);
  }

  downloadChatbotLogo(chatbotId: string): Promise<Blob | null | undefined> {
    return chatbotCrudOps.downloadChatbotLogo(this.chatbotApi, chatbotId);
  }

  uploadChatbotLogo(
    chatbotId: string,
    file: File,
  ): Promise<ChatbotWithIdentity> {
    return chatbotCrudOps.uploadChatbotLogo(this.chatbotApi, chatbotId, file);
  }

  deleteChatbotLogo(chatbotId: string): Promise<void> {
    return chatbotCrudOps.deleteChatbotLogo(this.chatbotApi, chatbotId);
  }

  listConversations(
    chatbotId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<ChatbotConversationWithIdentity[]> {
    return chatbotConversationOps.listConversations(
      this.chatbotApi,
      chatbotId,
      options,
    );
  }

  getConversation(
    chatbotId: string,
    conversationId: string,
  ): Promise<ChatbotConversationWithIdentity> {
    return chatbotConversationOps.getConversation(
      this.chatbotApi,
      chatbotId,
      conversationId,
    );
  }

  listConversationMessages(
    chatbotId: string,
    conversationId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<ChatbotMessageWithIdentity[]> {
    return chatbotConversationOps.listConversationMessages(
      this.chatbotApi,
      chatbotId,
      conversationId,
      options,
    );
  }

  listTrainingExamples(
    chatbotId: string,
    options: {
      limit?: number;
      offset?: number;
      search?: string;
      sort?: string;
    } = {},
  ): Promise<ChatbotTrainingExampleWithIdentity[]> {
    return chatbotTrainingOps.listTrainingExamples(
      this.chatbotApi,
      chatbotId,
      options,
    );
  }

  listAllTrainingExamples(
    chatbotId: string,
    onProgress?: (progress: { completed: number; total: number }) => void,
  ): Promise<ChatbotTrainingExampleWithIdentity[]> {
    return chatbotTrainingOps.listAllTrainingExamples(
      this.chatbotApi,
      chatbotId,
      onProgress,
    );
  }

  createTrainingExample(input: {
    chatbotId: string;
    question: string;
    answer: string;
    sortOrder?: number | null;
  }): Promise<ChatbotTrainingExampleWithIdentity> {
    return chatbotTrainingOps.createTrainingExample(this.chatbotApi, input);
  }

  readTrainingExample(
    chatbotId: string,
    exampleId: string,
  ): Promise<ChatbotTrainingExampleWithIdentity> {
    return chatbotTrainingOps.readTrainingExample(
      this.chatbotApi,
      chatbotId,
      exampleId,
    );
  }

  updateTrainingExample(input: {
    chatbotId: string;
    example: ChatbotTrainingExampleWithIdentity;
    question: string;
    answer: string;
    sortOrder: number | null;
  }): Promise<ChatbotTrainingExampleWithIdentity> {
    return chatbotTrainingOps.updateTrainingExample(this.chatbotApi, input);
  }

  deleteTrainingExample(chatbotId: string, exampleId: string): Promise<void> {
    return chatbotTrainingOps.deleteTrainingExample(
      this.chatbotApi,
      chatbotId,
      exampleId,
    );
  }

  deleteChatbot(chatbotId: string): Promise<void> {
    return chatbotCrudOps.deleteChatbot(this.chatbotApi, chatbotId);
  }

  listMembers(chatbotId: string): Promise<ChatbotMembershipWithIdentity[]> {
    return chatbotMembershipOps.listMembers(this.chatbotApi, chatbotId);
  }

  inviteMember(
    chatbotId: string,
    userId: string,
    role: ChatbotMemberRole,
  ): Promise<void> {
    return chatbotMembershipOps.inviteMember(
      this.chatbotApi,
      chatbotId,
      userId,
      role,
    );
  }

  updateMemberRole(
    chatbotId: string,
    member: ChatbotMembership,
    role: ChatbotMemberRole,
  ): Promise<void> {
    return chatbotMembershipOps.updateMemberRole(
      this.chatbotApi,
      chatbotId,
      member,
      role,
    );
  }

  removeMember(chatbotId: string, memberId: string): Promise<void> {
    return chatbotMembershipOps.removeMember(
      this.chatbotApi,
      chatbotId,
      memberId,
    );
  }
}

export const chatbotService = new ChatbotService();
